import React, { ReactElement, useEffect, useState } from "react";
import {
    ActionFunction, AsyncObjectType, FormField, FormFieldReplacementInput, FormFieldType, WorkflowFunctionWithParameters,
} from "../../../__generated__/api";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import FieldSelector, { SelectedGroup } from "../../pickers/field-selector/FieldSelector";
import { ReplacementGroup } from "../../replacement-group";
import Select from "../../select/Select";
import { ActionProps } from "./base";

const SOURCE_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,source)";
const FUNCTION_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,function)";

const TEXT_TYPES = new Set([FormFieldType.Text, FormFieldType.LongText, FormFieldType.SearchText]);
const DATE_TYPES = new Set([FormFieldType.Date, FormFieldType.Time, FormFieldType.DateTime]);
const JSON_TYPES = new Set([FormFieldType.Json]);

const FUNCTIONS = [
    {
        name: "Replace",
        description: "Replaces some text with a different text",
        type: ActionFunction.Replace,
        targetTypes: TEXT_TYPES,
        parameters: [
            {
                name: "Text",
                description: "What text to search for",
                isRequired: true,
                type: FormFieldType.Text,
                placeholder: "Text to search for...",
            },
            {
                name: "Flags",
                description: "Regular expression flags",
                isRequired: false,
                type: FormFieldType.Text,
                placeholder: "gi",
            },
            {
                name: "Replace Value",
                description: "The value to replace",
                isRequired: true,
                type: FormFieldType.Text,
                placeholder: "Replacement text",
            },
        ] as FormField[],

    },
    {
        name: "Substring",
        description: "Takes out a segment of string staring at one index and going to the last.",
        type: ActionFunction.Substring,
        targetTypes: TEXT_TYPES,
        parameters: [
            {
                name: "Start",
                description: "What character index to start taking a substring",
                isRequired: true,
                type: FormFieldType.Number,
            },
            {
                name: "Length",
                description: "The length of characters to take.",
                isRequired: false,
                type: FormFieldType.Number,
            },
        ] as FormField[],
    },
    {
        name: "Regular Expression",
        description: "Find a item with regular expression",
        type: ActionFunction.RegexpMatch,
        targetTypes: TEXT_TYPES,
        parameters: [
            {
                name: "Text",
                description: "What text to search for",
                isRequired: true,
                type: FormFieldType.Text,
                placeholder: "[a-z]*",
            },
            {
                name: "Flags",
                description: "Regular expression flags",
                isRequired: false,
                type: FormFieldType.Text,
                placeholder: "gi",
            },
            {
                name: "Index",
                description: "The index to find",
                isRequired: true,
                type: FormFieldType.Number,
                placeholder: "2",
            },
        ] as FormField[],

    },
    {
        name: "Upper Case",
        description: "Change the string to upper case",
        type: ActionFunction.UpperCase,
        targetTypes: TEXT_TYPES,
        parameters: [] as FormField[],
    },
    {
        name: "Lower Case",
        description: "Change the string to lower case",
        type: ActionFunction.LowerCase,
        targetTypes: TEXT_TYPES,
        parameters: [] as FormField[],
    },
    {
        name: "Capital Case",
        description: "Change the string to capital case",
        type: ActionFunction.CapitalCase,
        targetTypes: TEXT_TYPES,
        parameters: [] as FormField[],
    },
    {
        name: "Format Date",
        description: "Formats the input date",
        type: ActionFunction.DateFormat,
        targetTypes: DATE_TYPES,
        parameters: [
            {
                name: "Format",
                description: "What date format ot use",
                isRequired: true,
                type: FormFieldType.Text,
                placeholder: "mm/dd/yyyy",
            },
            {
                name: "Timezone",
                description: "What timezone should the date be expressed? If not specified, will use UTC",
                isRequired: false,
                type: FormFieldType.AsyncDropdown,
                asyncObjectType: AsyncObjectType.Timezone,
            },
        ] as FormField[],

    },
    {
        name: "Pad Start",
        description: "Adds changes the length of the current text to a desired length by "
                + "padding the start of the string with a specified value.",
        type: ActionFunction.PadStart,
        targetTypes: TEXT_TYPES,
        parameters: [
            {
                name: "Length",
                description: "What is the length of the new string",
                isRequired: true,
                type: FormFieldType.Number,
                placeholder: "5",
            },
            {
                name: "Pad string",
                description: "What is the value to insert?",
                isRequired: false,
                type: FormFieldType.Text,
                placeholder: "0",
            },
        ] as FormField[],

    },
    {
        name: "Pad End",
        description: "Adds changes the length of the current text to a desired length by "
                + "padding the end of the string with a specified value.",
        type: ActionFunction.PadEnd,
        targetTypes: TEXT_TYPES,
        parameters: [
            {
                name: "Length",
                description: "What is the length of the new string",
                isRequired: true,
                type: FormFieldType.Number,
                placeholder: "5",
            },
            {
                name: "Pad string",
                description: "What is the value to insert?",
                isRequired: false,
                type: FormFieldType.Text,
                placeholder: "0",
            },
        ] as FormField[],

    },
    {
        name: "JSON Path",
        description: "Extract a section of a JSON object.",
        type: ActionFunction.JsonPath,
        targetTypes: JSON_TYPES,
        parameters: [
            {
                name: "Path",
                description: "What is the path you want to extract?",
                isRequired: true,
                type: FormFieldType.Text,
                placeholder: "$.data",
            },
        ] as FormField[],

    },
    {
        name: "Join",
        description: "Joins items in an array using the character you provide.",
        type: ActionFunction.JsonJoin,
        targetTypes: JSON_TYPES,
        parameters: [
            {
                name: "Character",
                description: "What is the joining character? If not specified, will use a new line.",
                type: FormFieldType.Text,
                placeholder: "[New Line]",
            },
        ] as FormField[],

    },
];

export default function FormatSettings(props: ActionProps): ReactElement {
    const fieldIdInput = props.values.find(row => row.value.fieldId === SOURCE_ID)?.value.string;

    const functionInput = props.values.find(row => row.value.fieldId === FUNCTION_ID)?.value.string;

    const [selectedField, setSelectedField] = useState<SelectedGroup>();
    const [workflowFunction, setWorkflowFunction] = useState<WorkflowFunctionWithParameters>();

    useEffect(() => {
        if (selectedField && workflowFunction != null) {
            props.onChange({
                values: [
                    {
                        fieldId: SOURCE_ID,
                        type: FormFieldType.Text,
                        displayValue: selectedField.field.name,
                        string: JSON.stringify({
                            fieldId: selectedField.field.id,
                            groupId: selectedField.group.id,
                        } as FormFieldReplacementInput),
                    },
                    {
                        fieldId: FUNCTION_ID,
                        type: FormFieldType.Text,
                        displayValue: workflowFunction.function,
                        string: JSON.stringify(workflowFunction),
                    },
                ],
            });
        }
    }, [selectedField?.field.id, workflowFunction]);

    useEffect(() => {
        const fieldGroup = fieldIdInput ? JSON.parse(fieldIdInput) as FormFieldReplacementInput : undefined;
        const requestGroup = (fieldGroup && props.input)
            ? props.input.find(group => group.id === fieldGroup?.groupId) as ReplacementGroup
            : undefined;

        const field = fieldIdInput ? requestGroup?.input?.find(row => row?.id === fieldGroup?.fieldId) : undefined;
        if (field && requestGroup && field?.id !== selectedField?.field.id) {
            setSelectedField({
                field,
                group: requestGroup,
            });
        }
    }, [fieldIdInput]);

    useEffect(() => {
        if (functionInput) {
            setWorkflowFunction(JSON.parse(functionInput as string) as WorkflowFunctionWithParameters);
        }
    }, [functionInput]);

    const allowedFunctions = !selectedField
        ? []
        : FUNCTIONS?.filter(row => row.targetTypes.has(selectedField.field?.type)).map(row => ({
            label: row.name,
            value: row.type,
            data: row.type,
        }));

    const selectedFunction = allowedFunctions.find(func => func.data === workflowFunction?.function);

    const selectedFunctionParams = FUNCTIONS.find(func => func.type === workflowFunction?.function);
    return (
        <div className="">
            <div>
                <div className="">
                    Field
                </div>

                <div className="">
                    <FieldSelector
                        onChange={value => {
                            const curr = selectedField;
                            setSelectedField(value);

                            if (curr?.field.type !== value?.field.type) {
                                const firstAllowed = FUNCTIONS
                                    .find(row => row.targetTypes.has(value?.field.type as FormFieldType));
                                setWorkflowFunction({
                                    function: firstAllowed?.type,
                                    parameters: [],
                                } as WorkflowFunctionWithParameters);
                            }
                        }}
                        value={selectedField}
                        input={props?.input}
                    />
                </div>
                <small>
                    Select which field you want to format
                </small>
            </div>
            {selectedField && (
                <div>
                    {allowedFunctions.isNotEmpty ? (
                        <>
                            <div className="mt-3">
                                Format
                            </div>

                            <div className="">
                                <Select
                                    options={allowedFunctions ?? []}
                                    onChange={value => {
                                        setWorkflowFunction({
                                            function: value.data,
                                            parameters: [],
                                        });
                                    }}
                                    value={selectedFunction}
                                />
                            </div>
                            <small>
                                What is the function you want to apply?
                            </small>
                        </>
                    ) : (
                        <div className="mt-3 text-light">
                            No formatting available for this field type.
                        </div>
                    )}
                    {selectedFunctionParams && (
                        <div className="mt-4 mb-2">
                            <h5>
                                {selectedFunctionParams.name}
                            </h5>
                            {selectedFunctionParams.description}
                        </div>
                    )}

                    {selectedFunctionParams?.parameters?.map((param, index) => (
                        <FieldTypeValueDisplay
                            shouldShowFieldName
                            className="mb-3"
                            inputClassName="form-control"
                            field={param as FormField}
                            value={{
                                type: param.type,
                                string: workflowFunction?.parameters[index],
                                number: workflowFunction?.parameters[index]
                                    ? parseInt(workflowFunction.parameters[index], 10)
                                    : undefined,
                                fieldId: param.id,
                            }}
                            onChange={value => {
                                const parameters = [
                                    ...(workflowFunction?.parameters ?? []),
                                ];
                                parameters[index] = (value.string ?? value.number?.toString()) as string;
                                setWorkflowFunction({
                                    function: workflowFunction?.function,
                                    parameters,
                                });
                            }}
                        />

                    ))}
                </div>
            )}

        </div>

    );
}
